import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { LuBanknote, LuCalculator, LuLoaderCircle, LuSave } from 'react-icons/lu';
import { db } from '../../../config/firebaseConfig';
import { Field, TextInput } from '../../../components/form/FormControls';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { calculateGrossFromNet } from '../salaryMath';
import useEmployeeFinanceText from './employeeFinanceText';

const FORM_ID = 'salary-form';

/**
 * New / edit salary record (Salaries). The net amount is entered; gross,
 * deductions and employer cost are calculated from it (income tax brackets),
 * as before. Also opened from Maaş Ödemeleri › Düzenle.
 */
const AddSalaryModal = ({ isOpen = true, onClose, onSalaryAdded, onSalaryUpdated, employeeId, editingSalary, employeeName }) => {
  const { language, label, formatMoney } = useEmployeeFinanceText();
  const [formData, setFormData] = useState(() => ({
    contractDate: editingSalary?.contractDate || '',
    grossSalary: editingSalary?.grossSalary || '',
    netSalary: editingSalary?.netSalary || '',
    totalDeductions: editingSalary?.totalDeductions || '',
    suggestedCost: editingSalary?.suggestedCost || '',
  }));
  const [saving, setSaving] = useState(false);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'netSalary') {
      const calculations = calculateGrossFromNet(Number(value));
      setFormData((prev) => ({
        ...prev,
        grossSalary: calculations.grossSalary,
        totalDeductions: calculations.totalDeductions,
        suggestedCost: calculations.totalEmployerCost,
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.contractDate || !formData.netSalary) {
      toast.error(label('salaryForm.required'));
      return;
    }

    setSaving(true);
    try {
      const timestamp = new Date().getTime();
      const salaryData = {
        employeeId,
        ...formData,
        contractDate: formData.contractDate,
      };

      if (editingSalary && editingSalary.id) {
        // Update existing salary
        const salaryRef = doc(db, 'Salaries', editingSalary.id);
        await updateDoc(salaryRef, { ...salaryData, updatedAt: timestamp });
        onSalaryUpdated?.({ ...editingSalary, id: editingSalary.id, ...salaryData, updatedAt: timestamp });
        toast.success(label('salaryForm.updated'));
      } else {
        // Add new salary
        const newSalaryData = { ...salaryData, createdAt: timestamp };
        const docRef = await addDoc(collection(db, 'Salaries'), newSalaryData);
        onSalaryAdded?.({ id: docRef.id, ...newSalaryData });
        toast.success(label('salaryForm.added'));
      }

      onClose();
    } catch (error) {
      console.error('Maaş işlenirken hata oluştu:', error);
      toast.error(label('salaryForm.error'));
    } finally {
      setSaving(false);
    }
  };

  const isEditing = Boolean(editingSalary);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      dismissible={!saving}
      title={isEditing ? label('salaryForm.editTitle') : label('salaryForm.newTitle')}
      subtitle={employeeName || label('salaryForm.subtitle')}
      closeLabel={label('common.cancel')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30" size="xs">
          <LuBanknote aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>{label('common.cancel')}</Button>
          <Button variant="primary" type="submit" form={FORM_ID} disabled={saving}>
            {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {isEditing ? label('common.update') : label('common.save')}
          </Button>
        </>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit} className="tw-space-y-4">
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <Field label={label('salaryForm.contractDate')} htmlFor="salary-contract-date" required>
            <TextInput
              id="salary-contract-date"
              type="date"
              value={formData.contractDate}
              onChange={(event) => handleChange('contractDate', event.target.value)}
              required
            />
          </Field>
          <Field label={label('salaryForm.net')} htmlFor="salary-net" required hint={label('salaryForm.netHint')}>
            <TextInput
              id="salary-net"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={formData.netSalary}
              onChange={(event) => handleChange('netSalary', event.target.value)}
              placeholder="0.00"
              required
              className="tw-font-semibold tw-tabular-nums"
            />
          </Field>
        </div>

        {formData.grossSalary ? (
          <section aria-label={label('salaryForm.result')} className="tw-rounded-2xl tw-border tw-border-emerald-500/20 tw-bg-emerald-500/[0.06] tw-p-4">
            <p className="tw-mb-3 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-emerald-700 dark:tw-text-emerald-300">
              <LuCalculator aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
              {label('salaryForm.result')}
            </p>
            <dl className="tw-grid tw-gap-3 sm:tw-grid-cols-3">
              {[
                ['gross', formData.grossSalary],
                ['deductions', formData.totalDeductions],
                ['cost', formData.suggestedCost],
              ].map(([key, value]) => (
                <div key={key} className="tw-min-w-0">
                  <dt className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label(`salaryForm.${key}`)}</dt>
                  <dd className="tw-mt-0.5 tw-truncate tw-text-base tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{formatMoney(value)}</dd>
                </div>
              ))}
            </dl>
          </section>
        ) : (
          <p className="tw-rounded-2xl tw-border tw-border-dashed tw-border-slate-300 tw-px-4 tw-py-3 tw-text-sm tw-text-slate-500 dark:tw-border-white/15 dark:tw-text-slate-400">
            {label('salaryForm.empty')}
          </p>
        )}
      </form>
    </Modal>
  );
};

AddSalaryModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSalaryAdded: PropTypes.func,
  onSalaryUpdated: PropTypes.func,
  employeeId: PropTypes.string.isRequired,
  editingSalary: PropTypes.object,
  employeeName: PropTypes.string,
};

export default AddSalaryModal;
